// Renderer markdown estendido pra Biblioteca de Redação (caderno).
// O renderBlock genérico do projeto só faz parágrafo + inline; aqui ampliamos
// pra suportar headings (## / ###), listas (- / *, 1.), blockquotes (>),
// separadores (---). Inline (negrito/itálico/links) delega pro renderInline.
//
// [[project-essay-themes-library]]

import { renderInline } from "./markdown";

type Block =
  | { kind: "h"; level: 2 | 3 | 4; content: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "blockquote"; lines: string[] }
  | { kind: "hr" }
  | { kind: "p"; lines: string[] };

function flushPara(buf: string[], out: Block[]) {
  if (buf.length > 0) {
    out.push({ kind: "p", lines: buf.slice() });
    buf.length = 0;
  }
}

function flushList(
  buf: string[],
  out: Block[],
  kind: "ul" | "ol"
) {
  if (buf.length > 0) {
    out.push({ kind, items: buf.slice() } as Block);
    buf.length = 0;
  }
}

function flushQuote(buf: string[], out: Block[]) {
  if (buf.length > 0) {
    out.push({ kind: "blockquote", lines: buf.slice() });
    buf.length = 0;
  }
}

function tokenize(raw: string): Block[] {
  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  const out: Block[] = [];
  const paraBuf: string[] = [];
  let listBuf: string[] = [];
  let listKind: "ul" | "ol" | null = null;
  const quoteBuf: string[] = [];

  function closeAll() {
    flushPara(paraBuf, out);
    if (listKind) {
      flushList(listBuf, out, listKind);
      listKind = null;
    }
    flushQuote(quoteBuf, out);
  }

  for (const line of lines) {
    const trimmed = line.trimEnd();

    // Linha em branco → fecha parágrafo/lista/quote correntes
    if (trimmed.trim() === "") {
      closeAll();
      continue;
    }

    // Separador horizontal: ---, ***, ___
    if (/^(\s*)([-*_])\s*\2\s*\2[\s\S]*$/.test(trimmed) && /^[\s\-*_]+$/.test(trimmed)) {
      closeAll();
      out.push({ kind: "hr" });
      continue;
    }

    // Heading (## / ### / ####)
    const hMatch = /^(#{2,4})\s+(.+)$/.exec(trimmed);
    if (hMatch) {
      closeAll();
      const level = hMatch[1].length as 2 | 3 | 4;
      out.push({ kind: "h", level, content: hMatch[2] });
      continue;
    }

    // Blockquote
    const qMatch = /^>\s?(.*)$/.exec(trimmed);
    if (qMatch) {
      flushPara(paraBuf, out);
      if (listKind) {
        flushList(listBuf, out, listKind);
        listKind = null;
      }
      quoteBuf.push(qMatch[1]);
      continue;
    } else if (quoteBuf.length > 0) {
      flushQuote(quoteBuf, out);
    }

    // Lista não-ordenada (- ou *)
    const ulMatch = /^[-*]\s+(.+)$/.exec(trimmed);
    if (ulMatch) {
      flushPara(paraBuf, out);
      if (listKind === "ol") {
        flushList(listBuf, out, "ol");
        listKind = null;
      }
      listKind = "ul";
      listBuf.push(ulMatch[1]);
      continue;
    }

    // Lista ordenada (1. 2. etc.)
    const olMatch = /^\d+\.\s+(.+)$/.exec(trimmed);
    if (olMatch) {
      flushPara(paraBuf, out);
      if (listKind === "ul") {
        flushList(listBuf, out, "ul");
        listKind = null;
      }
      listKind = "ol";
      listBuf.push(olMatch[1]);
      continue;
    }

    // Fecha lista corrente se a linha não é item de lista
    if (listKind) {
      flushList(listBuf, out, listKind);
      listKind = null;
    }

    // Linha de parágrafo
    paraBuf.push(trimmed);
  }

  closeAll();
  return out;
}

function renderBlocks(blocks: Block[]): string {
  return blocks
    .map((b) => {
      if (b.kind === "hr") return '<hr class="essay-hr" />';
      if (b.kind === "h") {
        const tag = `h${b.level}`;
        return `<${tag} class="essay-h essay-h-${b.level}">${renderInline(b.content)}</${tag}>`;
      }
      if (b.kind === "p") {
        const html = b.lines.map(renderInline).join("<br>");
        return `<p class="essay-p">${html}</p>`;
      }
      if (b.kind === "blockquote") {
        const html = b.lines.map(renderInline).join("<br>");
        return `<blockquote class="essay-bq">${html}</blockquote>`;
      }
      if (b.kind === "ul") {
        return `<ul class="essay-ul">${b.items
          .map((it) => `<li>${renderInline(it)}</li>`)
          .join("")}</ul>`;
      }
      if (b.kind === "ol") {
        return `<ol class="essay-ol">${b.items
          .map((it) => `<li>${renderInline(it)}</li>`)
          .join("")}</ol>`;
      }
      return "";
    })
    .join("");
}

/** Renderiza markdown estendido pro caderno de redação. Suporta:
 *  - Parágrafos (linha em branco)
 *  - Headings ## / ### / ####
 *  - Listas - / * / 1.
 *  - Blockquote >
 *  - Separador ---
 *  - Inline (negrito, itálico, links) via renderInline genérico
 *
 *  Toda saída usa a fonte do projeto (Inter) — sem font-serif. O "feel" de
 *  prova vem do fundo creme/sépia, line-height e spacing definidos no CSS
 *  via classes `essay-*` (apps/ui/src/styles/globals.css). */
export function renderEssayMarkdown(raw: string): string {
  if (!raw) return "";
  return renderBlocks(tokenize(raw));
}
